import type {
  address,
  organization,
  SelectInvoice,
  SelectMembership,
  SelectPrice,
  SelectVoucher,
  user,
} from '@package/drizzle/schema'
import type { xendit } from '@package/sdk/xendit'

import { XENDIT_EXPIRY_INVOICE_IN_HR_ADD_ON, XENDIT_EXPIRY_INVOICE_IN_HR_NEW } from '../core/constant'
import { splitName } from '../format'
import { xenditInvoiceDescription } from './xendit-invoice-description'
import { invoiceDuration, invoiceDurationFromExpiryDate } from './xendit-invoice-duration'

/**
 * Extract types from Drizzle schema
 */
type SelectAddress = typeof address.$inferSelect
type SelectOrganization = typeof organization.$inferSelect
type SelectUser = typeof user.$inferSelect

/**
 * Extract types from Xendit SDK
 */
type XenditInvoiceCreateParams = Parameters<typeof xendit.Invoice.createInvoice>[0]['data']
type XenditInvoiceResponse = Awaited<ReturnType<typeof xendit.Invoice.createInvoice>>

/**
 * Notification channels supported by Xendit
 */
export type NotificationChannel = 'email' | 'whatsapp' | 'sms'

/**
 * Customer information for Xendit invoice
 */
export interface XenditCustomerInfo {
  firstName: string
  lastName?: string | null
  email: string
  phoneNumber?: string | null
}

/**
 * Address information for Xendit invoice
 */
export interface XenditAddressInfo {
  addressLineOne: string
  regency: string
  province: string
  postalCode: string
  country?: string
}

/**
 * Re-export Xendit SDK types for convenience
 */
export type XenditInvoiceItem = NonNullable<XenditInvoiceCreateParams['items']>[number]
export type XenditInvoiceFee = NonNullable<XenditInvoiceCreateParams['fees']>[number]
export type XenditInvoicePayload = XenditInvoiceCreateParams
export type { XenditInvoiceResponse }

/**
 * Props for creating a Xendit invoice payload
 */
export interface CreateXenditInvoicePayloadProps {
  /** Unique external ID for the invoice */
  externalId: string

  /** Total amount to be paid */
  amount: number

  /** Invoice duration in seconds */
  invoiceDuration: number

  /** Invoice description */
  description: string

  /** Customer information */
  customer: XenditCustomerInfo

  /** Customer address (optional) */
  address?: XenditAddressInfo | null

  /** Success redirect URL */
  successRedirectUrl: string

  /** Failure redirect URL */
  failureRedirectUrl: string

  /** Invoice items */
  items: XenditInvoiceItem[]

  /** Additional fees (optional) */
  fees?: XenditInvoiceFee[]

  /** Currency code (default: 'IDR') */
  currency?: string

  /** Locale (default: 'ID') */
  locale?: string

  /** Whether to send email notification (default: true) */
  shouldSendEmail?: boolean

  /** Custom notification preferences (optional) */
  customNotificationPreference?: {
    invoiceCreated?: NotificationChannel[]
    invoiceReminder?: NotificationChannel[]
    invoicePaid?: NotificationChannel[]
  }
}

/**
 * Creates a properly formatted Xendit invoice payload with all required fields.
 * Automatically handles notification preferences based on customer phone number availability.
 *
 * @param props - Invoice payload properties
 * @returns Complete Xendit invoice payload ready to be sent to Xendit API
 *
 * @example
 * ```typescript
 * const payload = createXenditInvoicePayload({
 *   externalId: 'inv-123',
 *   amount: 100000,
 *   invoiceDuration: 86400, // 24 hours in seconds
 *   description: 'Premium subscription',
 *   customer: {
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     email: 'john@example.com',
 *     phoneNumber: '+6281234567890'
 *   },
 *   successRedirectUrl: 'https://example.com/success',
 *   failureRedirectUrl: 'https://example.com/failed',
 *   items: [{
 *     name: 'Premium Plan',
 *     quantity: 1,
 *     price: 100000,
 *     referenceId: 'plan-premium-001'
 *   }]
 * })
 * ```
 */
export function createXenditInvoicePayload(props: CreateXenditInvoicePayloadProps): XenditInvoicePayload {
  const {
    externalId,
    amount,
    invoiceDuration,
    description,
    customer,
    address,
    successRedirectUrl,
    failureRedirectUrl,
    items,
    fees,
    currency = 'IDR',
    locale = 'ID',
    shouldSendEmail = true,
    customNotificationPreference,
  } = props

  // Determine notification channels based on phone number availability
  const defaultNotificationChannels: NotificationChannel[] = customer.phoneNumber ? ['whatsapp', 'email'] : ['email']

  // Build customer object
  const customerPayload: NonNullable<XenditInvoicePayload['customer']> = {
    givenNames: customer.firstName,
    email: customer.email,
  }

  // Add optional customer fields
  if (customer.lastName) {
    customerPayload.surname = customer.lastName
  }

  if (customer.phoneNumber) {
    customerPayload.mobileNumber = customer.phoneNumber
  }

  // Add address if provided
  if (address) {
    customerPayload.addresses = [
      {
        city: address.regency,
        country: address.country ?? 'Indonesia',
        postalCode: address.postalCode,
        state: address.province,
        streetLine1: address.addressLineOne,
      },
    ]
  }

  // Build the complete payload
  const payload: XenditInvoicePayload = {
    externalId,
    amount,
    invoiceDuration,
    description,
    customer: customerPayload,
    shouldSendEmail,
    customerNotificationPreference: {
      invoiceCreated: customNotificationPreference?.invoiceCreated ?? defaultNotificationChannels,
      invoiceReminder: customNotificationPreference?.invoiceReminder ?? defaultNotificationChannels,
      invoicePaid: customNotificationPreference?.invoicePaid ?? defaultNotificationChannels,
    },
    successRedirectUrl,
    failureRedirectUrl,
    currency,
    items,
    locale,
  }

  // Add fees if provided
  if (fees && fees.length > 0) {
    payload.fees = fees
  }

  return payload
}

/**
 * Helper context for building invoice from database records
 */
export interface BuildInvoicePayloadContext {
  /** Invoice record from database */
  invoice: SelectInvoice

  /** Organization record from database */
  organization: SelectOrganization

  /** Main user record from database */
  user: SelectUser

  /** Optional address record from database */
  address?: SelectAddress | null

  /** Optional membership record (for ADD-ON invoices) */
  membership?: SelectMembership | null

  /** Success redirect URL */
  successRedirectUrl: string

  /** Failure redirect URL */
  failureRedirectUrl: string

  /** Price record from database (required for NEW/RENEWAL invoices) */
  price?: SelectPrice

  /** Optional fees to add to the invoice */
  fees?: XenditInvoiceFee[]

  /** Optional voucher record from database */
  voucher?: SelectVoucher

  /** Optional items to add to the invoice. Will overwrite the items generated from the description */
  items?: XenditInvoiceItem[]

  /**
   * RENEWAL only: how many of the invoice's seats are EMPTY seats starting at payment and charged
   * pro-rata until the handover. Named in the gateway description; see `xenditInvoiceDescription`.
   */
  renewalBridgedSeats?: number

  /**
   * Optional: Invoice duration configuration. If not provided, defaults will be used:
   * - NEW/RENEWAL: 24 hours from now
   * - ADD-ON: 1 hour before membership end date
   *
   * Can be:
   * - number: Explicit duration in seconds (e.g., 86400 for 24 hours)
   * - { hours: number }: Duration in hours (e.g., { hours: 48 })
   * - { days: number }: Duration in days (e.g., { days: 7 })
   *
   * @example
   * ```typescript
   * // Explicit seconds
   * invoiceDuration: 172800  // 2 days
   *
   * // Duration config
   * invoiceDuration: { hours: 48 }  // 48 hours from now (NEW/RENEWAL) or before end date (ADD-ON)
   * invoiceDuration: { days: 7 }    // 7 days from now (NEW/RENEWAL) or before end date (ADD-ON)
   * ```
   */
  invoiceDuration?: number | { hours: number } | { days: number }
}

/**
 * Higher-level helper that builds complete Xendit invoice payload from database records.
 * Automatically generates description, items, and maps database types to Xendit format.
 *
 * @param context - Business context with database records
 * @returns Complete Xendit invoice payload ready for API
 *
 * @example
 * ```typescript
 * // Basic usage with defaults (24h for NEW/RENEWAL, 1h before end for ADD-ON)
 * const payload = buildXenditInvoicePayload({
 *   invoice: invoiceRecord,
 *   organization: schoolRecord,
 *   user: userRecord,
 *   successRedirectUrl: 'https://...',
 *   failureRedirectUrl: 'https://...',
 *   price: priceRecord,
 * })
 *
 * // Custom duration - hours
 * const payload = buildXenditInvoicePayload({
 *   invoice: invoiceRecord,
 *   organization: schoolRecord,
 *   user: userRecord,
 *   successRedirectUrl: 'https://...',
 *   failureRedirectUrl: 'https://...',
 *   price: priceRecord,
 *   invoiceDuration: { hours: 48 },  // 48 hours from now or before end date
 * })
 *
 * // Custom duration - days
 * const payload = buildXenditInvoicePayload({
 *   invoice: invoiceRecord,
 *   organization: schoolRecord,
 *   user: userRecord,
 *   successRedirectUrl: 'https://...',
 *   failureRedirectUrl: 'https://...',
 *   price: priceRecord,
 *   invoiceDuration: { days: 7 },    // 7 days from now or before end date
 * })
 *
 * // Explicit seconds
 * const payload = buildXenditInvoicePayload({
 *   invoice: invoiceRecord,
 *   organization: schoolRecord,
 *   user: userRecord,
 *   successRedirectUrl: 'https://...',
 *   failureRedirectUrl: 'https://...',
 *   price: priceRecord,
 *   invoiceDuration: 172800,         // 2 days in seconds
 * })
 * ```
 */
export function buildXenditInvoicePayload(context: BuildInvoicePayloadContext): XenditInvoicePayload {
  const {
    invoice,
    organization,
    user,
    address,
    membership,
    invoiceDuration: durationInput,
    successRedirectUrl,
    failureRedirectUrl,
    price,
    fees,
    items,
    renewalBridgedSeats,
  } = context

  // Validate that price is provided for NEW and RENEWAL invoice types
  if ((invoice.type === 'NEW' || invoice.type === 'RENEWAL') && !price) {
    throw new Error(`Price is required for ${invoice.type} invoice type`)
  }

  // Validate that membership is provided for ADD-ON invoice type
  if (invoice.type === 'ADD-ON' && !membership?.endDate) {
    throw new Error('Membership with endDate is required for ADD-ON invoice type')
  }

  // Determine organization ID
  const organizationId = invoice.organizationId ?? null

  if (!organizationId) {
    throw new Error('Organization ID is required')
  }

  // Calculate invoice duration based on input type
  const calculatedInvoiceDuration = (() => {
    // If explicit number provided, use it directly
    if (typeof durationInput === 'number') {
      return durationInput
    }

    // If config object provided, use it
    if (durationInput) {
      // For ADD-ON: Calculate from membership end date with custom config
      if (invoice.type === 'ADD-ON' && membership?.endDate) {
        return invoiceDurationFromExpiryDate({
          expiryDate: membership.endDate,
          before: durationInput,
        })
      }
      // For NEW/RENEWAL: Calculate from now with custom config
      return invoiceDuration(durationInput)
    }

    // Use defaults
    if (invoice.type === 'ADD-ON' && membership?.endDate) {
      return invoiceDurationFromExpiryDate({
        expiryDate: membership.endDate,
        before: { hours: XENDIT_EXPIRY_INVOICE_IN_HR_ADD_ON },
      })
    }

    return invoiceDuration({ hours: XENDIT_EXPIRY_INVOICE_IN_HR_NEW })
  })()

  const orgName = organization.name
  const type = 'schoolId' in invoice ? 'school' : 'family'

  // Generate description and item details using helper
  const descriptionData =
    invoice.type === 'ADD-ON' && membership?.endDate
      ? xenditInvoiceDescription({
          invoiceType: 'ADD-ON',
          type,
          orgName,
          orgId: organizationId,
          invoiceNumber: invoice.invoiceNumber,
          invoiceId: invoice.id,
          noOfSeats: invoice.seats,
          endDate: membership.endDate,
        })
      : invoice.type === 'RENEWAL'
        ? xenditInvoiceDescription({
            invoiceType: 'RENEWAL',
            type,
            orgName,
            orgId: organizationId,
            invoiceNumber: invoice.invoiceNumber,
            invoiceId: invoice.id,
            noOfSeats: invoice.seats,
            premiumDuration: price?.duration ?? 1,
            bridgedSeats: renewalBridgedSeats,
          })
        : xenditInvoiceDescription({
            invoiceType: 'NEW',
            type,
            orgName,
            orgId: organizationId,
            invoiceNumber: invoice.invoiceNumber,
            invoiceId: invoice.id,
            noOfSeats: invoice.seats,
            premiumDuration: price?.duration ?? 1,
          })

  // Build invoice items from description
  const invoiceItems: XenditInvoiceItem[] = items ?? [
    {
      name: descriptionData.itemName,
      quantity: 1,
      price: invoice.amountPaid,
      referenceId: descriptionData.referenceId,
    },
  ]

  // Map database address to Xendit format
  const xenditAddress: XenditAddressInfo | undefined =
    address?.regency && address.province && address.postalCode
      ? {
          addressLineOne: address.addressLineOne,
          regency: address.regency,
          province: address.province,
          postalCode: address.postalCode,
          country: address.country ?? undefined,
        }
      : undefined

  const { firstName, lastName } = splitName(user.name)

  // Map database user to Xendit format
  const xenditCustomer: XenditCustomerInfo = {
    firstName,
    lastName,
    email: user.email,
    phoneNumber: user.phoneNumber,
  }

  return createXenditInvoicePayload({
    externalId: invoice.id,
    amount: invoice.amountPaid,
    invoiceDuration: calculatedInvoiceDuration,
    description: descriptionData.description,
    customer: xenditCustomer,
    address: xenditAddress,
    successRedirectUrl,
    failureRedirectUrl,
    items: invoiceItems,
    fees,
  })
}
